// DecisionLogTablePage.jsx — v1.0.8 Document 4 Step 6
//
// Standalone admin page rendered at /admin/decision-log. Filterable, paginated
// table backed by GET /api/admin/decision-log. Replaces the temporary
// raw-JSON popup that the v1.0.8 Step 3 component was opening.
//
// Filters (all six per spec):
//   app · decision_type · mode_used · confidence_tier · human_required · date range
//
// Pagination:
//   limit + offset query string driven; sticky in URL so a refresh keeps state
//   and copy/paste links preserve filters for collaboration.
//
// Empty state copy is intentionally explicit — the log stays empty until the
// universal Orchestrator at /library ships and writes its first record. This
// page is "ready and waiting" infrastructure, not a placeholder.
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Filter,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Database,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Confidence tier color tokens (re-uses admin dot palette).
const TIER_DOT = {
  high: "bg-emerald-400",
  medium: "bg-amber-400",
  low: "bg-red-500",
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export default function DecisionLogTablePage() {
  // URL-driven filter state — sticky across reloads, shareable via copy-paste.
  const [params, setParams] = useSearchParams();
  const filters = useMemo(
    () => ({
      app: params.get("app") || "",
      decision_type: params.get("decision_type") || "",
      mode_used: params.get("mode_used") || "",
      confidence_tier: params.get("confidence_tier") || "",
      human_required: params.get("human_required") || "",
      since: params.get("since") || "",
      until: params.get("until") || "",
      limit: parseInt(params.get("limit") || "50", 10),
      offset: parseInt(params.get("offset") || "0", 10),
    }),
    [params],
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // Build query string from filters (omit empty values for clean URLs).
  const buildQS = useCallback((f) => {
    const q = new URLSearchParams();
    if (f.app) q.set("app", f.app);
    if (f.decision_type) q.set("decision_type", f.decision_type);
    if (f.mode_used) q.set("mode_used", f.mode_used);
    if (f.confidence_tier) q.set("confidence_tier", f.confidence_tier);
    if (f.human_required) q.set("human_required", f.human_required);
    if (f.since) q.set("since", f.since);
    if (f.until) q.set("until", f.until);
    q.set("limit", String(f.limit));
    q.set("offset", String(f.offset));
    return q.toString();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQS(filters);
      const res = await axios.get(`${API}/admin/decision-log?${qs}`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setLastRefreshed(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, buildQS]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Mutate URL params (preserves stickiness + page-history navigation).
  const updateFilter = (patch, resetOffset = true) => {
    const next = { ...filters, ...patch };
    if (resetOffset) next.offset = 0;
    setParams(buildQS(next));
  };

  const clearFilters = () => {
    setParams(`limit=${filters.limit}&offset=0`);
  };

  const pageStart = filters.offset + 1;
  const pageEnd = Math.min(filters.offset + items.length, total);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition"
            data-testid="dlt-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <span className="text-slate-300">/</span>
          <h1 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-500" />
            Decision Log
          </h1>
          <span className="ml-auto flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-xs text-slate-400" data-testid="dlt-last-refreshed">
                Refreshed {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition px-2.5 py-1.5 rounded-md hover:bg-slate-100"
              data-testid="dlt-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* ── Filter bar ──────────────────────────────────────── */}
        <FilterBar filters={filters} updateFilter={updateFilter} clearFilters={clearFilters} />

        {/* ── Status row ─────────────────────────────────────── */}
        <div className="flex items-center justify-between text-xs text-slate-500" data-testid="dlt-status">
          {loading ? (
            <span><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…</span>
          ) : error ? (
            <span className="text-red-600">{error}</span>
          ) : total === 0 ? (
            <span>No matching decisions.</span>
          ) : (
            <span>
              Showing <strong>{pageStart}</strong>–<strong>{pageEnd}</strong> of{" "}
              <strong>{total.toLocaleString()}</strong>
            </span>
          )}
          <PageSizePicker
            value={filters.limit}
            onChange={(v) => updateFilter({ limit: v })}
          />
        </div>

        {/* ── Table ──────────────────────────────────────────── */}
        {!loading && total === 0 && !error ? (
          <EmptyState filtersActive={Object.values(filters).some((v, k) => v && v !== 50 && v !== 0)} />
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-xs" data-testid="dlt-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>App</Th>
                  <Th>Decision Type</Th>
                  <Th>Mode</Th>
                  <Th>Confidence</Th>
                  <Th>Human?</Th>
                  <Th>Sim?</Th>
                  <Th>Timestamp</Th>
                  <Th className="text-right pr-4">Trace</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((d, i) => (
                  <tr
                    key={d.trace_id || i}
                    className="hover:bg-slate-50 transition"
                    data-testid={`dlt-row-${i}`}
                  >
                    <Td>{d.app || "—"}</Td>
                    <Td className="font-medium text-slate-700">{d.decision_type || "—"}</Td>
                    <Td>{d.mode_used || "—"}</Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={`w-2 h-2 rounded-full ${TIER_DOT[d.confidence_tier] || "bg-slate-300"}`}
                        />
                        <span>{d.confidence != null ? Number(d.confidence).toFixed(2) : "—"}</span>
                        <span className="text-slate-400 capitalize">({d.confidence_tier || "—"})</span>
                      </span>
                    </Td>
                    <Td>
                      {d.human_required
                        ? <AlertCircle className="w-3.5 h-3.5 text-red-500 inline" />
                        : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline" />}
                    </Td>
                    <Td>{d.simulation_mode ? "yes" : "no"}</Td>
                    <Td className="text-slate-500">{fmtTime(d.timestamp)}</Td>
                    <Td className="text-right pr-4">
                      <code className="font-mono text-[10px] text-slate-400">
                        {(d.trace_id || "").slice(0, 8)}
                      </code>
                    </Td>
                  </tr>
                ))}
                {loading && Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`loading-${i}`}>
                    <td colSpan={8} className="px-4 py-3">
                      <div className="h-3 bg-slate-100 rounded animate-pulse" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ─────────────────────────────────────── */}
        {total > filters.limit && (
          <Pagination
            offset={filters.offset}
            limit={filters.limit}
            total={total}
            onChange={(o) => updateFilter({ offset: o }, /*resetOffset=*/ false)}
          />
        )}
      </main>
    </div>
  );
}


// ─── Filter bar ──────────────────────────────────────────────────

function FilterBar({ filters, updateFilter, clearFilters }) {
  const anyActive = !!(
    filters.app || filters.decision_type || filters.mode_used ||
    filters.confidence_tier || filters.human_required || filters.since || filters.until
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="dlt-filters">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Filters</span>
        {anyActive && (
          <button
            className="ml-auto text-[11px] text-slate-500 hover:text-slate-900 transition"
            onClick={clearFilters}
            data-testid="dlt-filter-clear"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FilterInput label="App" value={filters.app}
          onChange={(v) => updateFilter({ app: v })} testId="dlt-filter-app"
          placeholder="birthdaydeelight…" />
        <FilterInput label="Decision Type" value={filters.decision_type}
          onChange={(v) => updateFilter({ decision_type: v })} testId="dlt-filter-decision-type"
          placeholder="staging_to_active…" />
        <FilterSelect label="Mode" value={filters.mode_used}
          onChange={(v) => updateFilter({ mode_used: v })} testId="dlt-filter-mode"
          options={[
            { v: "", l: "All modes" },
            { v: "PRECISION", l: "Precision" },
            { v: "SPEED", l: "Speed" },
            { v: "EXPLORATION", l: "Exploration" },
            { v: "MAINTENANCE", l: "Maintenance" },
            { v: "CONSERVATIVE", l: "Conservative" },
          ]} />
        <FilterSelect label="Confidence Tier" value={filters.confidence_tier}
          onChange={(v) => updateFilter({ confidence_tier: v })} testId="dlt-filter-tier"
          options={[
            { v: "", l: "All tiers" },
            { v: "high", l: "High" },
            { v: "medium", l: "Medium" },
            { v: "low", l: "Low" },
          ]} />
        <FilterSelect label="Human Required" value={filters.human_required}
          onChange={(v) => updateFilter({ human_required: v })} testId="dlt-filter-human"
          options={[
            { v: "", l: "Either" },
            { v: "true", l: "Yes" },
            { v: "false", l: "No" },
          ]} />
        <FilterInput label="Since" type="date" value={filters.since}
          onChange={(v) => updateFilter({ since: v })} testId="dlt-filter-since" />
        <FilterInput label="Until" type="date" value={filters.until}
          onChange={(v) => updateFilter({ until: v })} testId="dlt-filter-until" />
      </div>
    </div>
  );
}

function FilterInput({ label, value, onChange, testId, placeholder, type = "text" }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-200"
      />
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-200 bg-white"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}


// ─── Pagination ──────────────────────────────────────────────────

function Pagination({ offset, limit, total, onChange }) {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const goPrev = () => onChange(Math.max(0, offset - limit));
  const goNext = () => onChange(Math.min((totalPages - 1) * limit, offset + limit));
  return (
    <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3" data-testid="dlt-pagination">
      <span className="text-xs text-slate-500">
        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
      </span>
      <div className="flex items-center gap-2">
        <button
          onClick={goPrev}
          disabled={offset === 0}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          data-testid="dlt-page-prev"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Previous
        </button>
        <button
          onClick={goNext}
          disabled={currentPage >= totalPages}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
          data-testid="dlt-page-next"
        >
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function PageSizePicker({ value, onChange }) {
  return (
    <label className="flex items-center gap-1.5 text-xs text-slate-500">
      <span>Page size</span>
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        data-testid="dlt-page-size"
        className="text-xs px-2 py-1 rounded-md border border-slate-200 bg-white"
      >
        {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </label>
  );
}


// ─── Empty state ─────────────────────────────────────────────────

function EmptyState({ filtersActive }) {
  return (
    <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-testid="dlt-empty-state">
      <Database className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <h3 className="text-sm font-bold text-slate-700 mb-1">
        {filtersActive ? "No decisions match these filters" : "Decision log is empty"}
      </h3>
      {filtersActive ? (
        <p className="text-xs text-slate-500 max-w-md mx-auto">
          Adjust or clear the filters above. The log records every Orchestrator decision —
          some filter combinations may simply have no matches yet.
        </p>
      ) : (
        <p className="text-xs text-slate-500 max-w-md mx-auto">
          The log stays empty until the universal Orchestrator at{" "}
          <code className="font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px]">
            /library/orchestrator
          </code>{" "}
          ships and writes its first record. This page is ready and waiting — nothing is broken.
        </p>
      )}
    </div>
  );
}


// ─── Cells ───────────────────────────────────────────────────────

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-2.5 text-slate-600 ${className}`}>{children}</td>;
}


// ─── Helpers ─────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}
